import Router from "next/router";
import { useEffect, useState } from "react";
import "react-simple-keyboard/build/css/index.css";
import { AppUpdateChecker } from "~/components/app-update-checker";
import { FeatureRestrictionChecker } from "~/components/feature-restriction-checker";
import { LogoutModal } from "~/components/logout-modal";
import { MaintenanceLayout } from "~/components/maintenance-layout";
import { OfflineDialog } from "~/components/offline-dialog";
import { OperationalNotification } from "~/components/operational-notification";
import { PushNotification } from "~/components/push-notification";
import { TimezoneMismatchChecker } from "~/components/timezone-mismatch-checker";
import { useRouterEvent } from "~/libs/google-analytics";
import { useNprogress } from "~/libs/nprogrss";
import { AppProvider } from "~/providers/app-provider";
import { queryClient, QueryProvider } from "~/providers/query-provider";
import { useIsReady } from "~/store";
import { AppToaster } from "~served/ui-comps/components/app-toaster";
import { ErrorBoundary } from "~served/ui-comps/components/error-boundary";
import { OverlayGhostHost } from "~served/ui-comps/components/overlay-presence/overlay-ghost-host";
import { SplashScreen } from "~served/ui-comps/components/splash-screen";
import "~/locales/i18n";
import { Clarity } from "~/components/clarity";
import { RoomMasterErrorDialog } from "~/components/room-master-error-dialog";
import type { AppPropsWithLayout } from "~/types/next";

export default function MyApp({
	Component,
	pageProps,
	router: appRouter,
}: AppPropsWithLayout) {
	useServiceWorker();
	useNprogress(appRouter);
	useRouterEvent();

	const isReady = useIsReady();
	const getLayout = Component.getLayout ?? ((page) => page);
	const isMounted = useIsMounted();

	if (!isMounted) return <SplashScreen />;

	return (
		<ErrorBoundary
			appName="STAFF APP"
			callback={() => {
				queryClient.clear();
			}}>
			<QueryProvider>
				<AppProvider>
					<Clarity />
					<AppToaster
						position="bottom-right"
						visibleToasts={99}
					/>
					<OverlayGhostHost />
					<AppUpdateChecker />
					<TimezoneMismatchChecker />
					<OfflineDialog />
					<LogoutModal />
					<FeatureRestrictionChecker />
					<PushNotification />
					<OperationalNotification />
					<RoomMasterErrorDialog />
					<MaintenanceLayout>
						{isReady ? (
							getLayout(<Component {...pageProps} />)
						) : (
							<SplashScreen />
						)}
					</MaintenanceLayout>
				</AppProvider>
			</QueryProvider>
		</ErrorBoundary>
	);
}

disableReactDevTools();

const useIsMounted = () => {
	const [isMounted, setIsMounted] = useState(false);

	useEffect(() => {
		setIsMounted(true);
	}, []);

	return isMounted;
};

const useServiceWorker = () => {
	useEffect(() => {
		const onReceiveServiceWorkerMessage = (event: MessageEvent) => {
			if (!event.data.action) return;

			switch (event.data.action) {
				case "redirect-from-notificationclick":
					void Router.replace(event.data.url);
					break;
			}
		};

		if (navigator.serviceWorker)
			navigator.serviceWorker.addEventListener(
				"message",
				onReceiveServiceWorkerMessage,
			);

		return () => {
			if (navigator.serviceWorker)
				navigator.serviceWorker.removeEventListener(
					"message",
					onReceiveServiceWorkerMessage,
				);
		};
	}, []);
};

function disableReactDevTools() {
	const isFunction = (obj: unknown) => {
		return typeof obj === "function";
	};

	const isObject = (obj: unknown) => {
		const type = typeof obj;
		return type === "function" || (type === "object" && !!obj);
	};

	const hasWindowObject = () => {
		return typeof window !== "undefined" && !!window.document;
	};

	if (hasWindowObject()) {
		window.DEBUG_REACT_DEV_TOOLS_ENABLED = false;
	}

	const isEnabledDevTools = process.env.NODE_ENV === "development";

	if (isEnabledDevTools) {
		if (hasWindowObject()) window.DEBUG_REACT_DEV_TOOLS_ENABLED = true;
		return;
	}

	if (hasWindowObject()) {
		if (!isObject(window.__REACT_DEVTOOLS_GLOBAL_HOOK__)) {
			return;
		}

		for (const prop in window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
			if (prop === "renderers") {
				window.__REACT_DEVTOOLS_GLOBAL_HOOK__[prop] = new Map();
				continue;
			}
			window.__REACT_DEVTOOLS_GLOBAL_HOOK__[prop] = isFunction(
				window.__REACT_DEVTOOLS_GLOBAL_HOOK__[prop],
			)
				? Function.prototype
				: null;
		}
	}
}

declare global {
	interface Window {
		DEBUG_REACT_DEV_TOOLS_ENABLED: boolean;
		__REACT_DEVTOOLS_GLOBAL_HOOK__: any;
	}

	interface Number {
		padStart(size: number): string;
	}
}

Number.prototype.padStart = function (size = 2) {
	let s = String(this);
	while (s.length < size) {
		s = "0" + s;
	}
	return s;
};
